import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import BackupRoundedIcon from "@mui/icons-material/BackupRounded";
import CloudQueueRoundedIcon from "@mui/icons-material/CloudQueueRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import api from "services/api";
import { useServerData } from "scenes/server/ServerDataProvider";
import {
  Panel,
  StatusChip,
  formatBytes,
  formatDuration,
  formatGB,
  formatMB,
  formatPercent,
  relativeTime,
  severityToken,
} from "scenes/server/serverUi";

/**
 * Server overview (2026-10-21).
 *
 * Reads the snapshot written by the system-cron collector. Health, issues and
 * fresh metrics come from the page-level provider (shared with the header);
 * the 24h trend and table statistics are fetched here on mount.
 */

// ── Presentational pieces ────────────────────────────────────────────────────

const MetricCard = ({ icon, title, value, subtitle, note, progress, children }) => {
  const theme = useTheme();

  const progressColor =
    progress === null || progress === undefined
      ? theme.palette.primary.main
      : progress >= 92
        ? theme.palette.error.main
        : progress >= 85
          ? theme.palette.warning.main
          : theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ color: theme.palette.primary.main, display: "flex" }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {value !== undefined && (
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </Typography>
      )}

      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}

      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ opacity: 0.85, mt: -0.5, wordBreak: "break-all" }}
        >
          {note}
        </Typography>
      )}

      {progress !== null && progress !== undefined && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, Number(progress)))}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: theme.palette.action.hover,
            "& .MuiLinearProgress-bar": { bgcolor: progressColor, borderRadius: 4 },
          }}
        />
      )}

      {children}
    </Paper>
  );
};

/** Dependency-free sparkline — 24h trend without pulling in a chart engine. */
const Sparkline = ({ values, height = 36 }) => {
  const theme = useTheme();
  const points = (values || []).filter((value) => value !== null && value !== undefined);

  const geometry = useMemo(() => {
    if (points.length < 2) return null;
    const max = 100;
    const min = 0;
    const width = 100;
    return points
      .map((value, index) => {
        const x = (index / (points.length - 1)) * width;
        const y = height - ((Number(value) - min) / (max - min)) * height;
        return `${x.toFixed(2)},${Math.max(0, Math.min(height, y)).toFixed(2)}`;
      })
      .join(" ");
  }, [points, height]);

  if (!geometry) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height }}
        aria-hidden="true"
      >
        <polyline
          points={geometry}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const ServerOverview = () => {
  const theme = useTheme();

  // Health, snapshot and issues come from the page-level provider, which is
  // also what the header renders — one fetch for the whole screen.
  const { snapshot, config, issues, error, loading, driveTrend } = useServerData();

  const [history, setHistory] = useState([]);
  const [database, setDatabase] = useState(null);

  // History and table statistics are specific to this tab and comparatively
  // expensive (the database endpoint scans information_schema), so they load
  // once on mount instead of on every 60s status poll.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [historyResponse, databaseResponse] = await Promise.all([
        api.get("/server/history", { params: { hours: 24 } }).catch(() => null),
        api.get("/server/database").catch(() => null),
      ]);

      if (cancelled) return;
      setHistory(historyResponse?.data?.series || []);
      setDatabase(databaseResponse?.data || null);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading && !snapshot) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const services = Array.isArray(snapshot?.Services) ? snapshot.Services : [];
  const certificates = Array.isArray(snapshot?.SslCertificates)
    ? snapshot.SslCertificates
    : [];
  const backups = snapshot?.Backups || null;
  const drive = backups?.remote || null;

  // 24h context under each headline metric, so "68%" is not the only number
  // on screen — a slow climb is visible without switching to History.
  const trend = (key) => {
    const values = history
      .map((point) => point[key])
      .filter((value) => value !== null && value !== undefined);

    if (values.length === 0) return null;

    const average =
      Math.round(
        (values.reduce((sum, value) => sum + value, 0) / values.length) * 10
      ) / 10;
    const peak = Math.max(...values);

    return `24h avg ${average}% · peak ${peak}%`;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 2 }}>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      {/* Issue list --------------------------------------------------------- */}
      {issues.length > 0 && (
        <Panel
          title={`Issues (${issues.length})`}
          subtitle="Reported by the latest collection"
          icon={<WarningRoundedIcon />}
          accent={
            issues.some((issue) => issue.severity === "critical")
              ? "critical"
              : "warning"
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {issues.map((issue, index) => {
              const { color, Icon } = severityToken(theme, issue.severity);

              return (
                <Box
                  key={`${issue.code}-${index}`}
                  sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                >
                  <Box sx={{ color, display: "flex", mt: "2px", flexShrink: 0 }}>
                    <Icon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{issue.message}</Typography>
                    {issue.code && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                      >
                        {issue.code}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Panel>
      )}

      {/* Metric cards ------------------------------------------------------- */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<MemoryRoundedIcon />}
            title="CPU"
            value={formatPercent(snapshot?.CpuPercent)}
            subtitle={
              snapshot ? `${snapshot.CpuCores ?? "?"} cores` : "No data"
            }
            progress={snapshot?.CpuPercent}
            note={trend("cpu")}
          >
            <Sparkline values={history.map((point) => point.cpu)} />
          </MetricCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<DnsRoundedIcon />}
            title="Memory"
            value={formatPercent(snapshot?.MemoryPercent)}
            subtitle={
              snapshot
                ? `${formatMB(snapshot.MemoryUsedMB)} of ${formatMB(snapshot.MemoryTotalMB)}${
                    snapshot.SwapTotalMB
                      ? ` · swap ${formatMB(snapshot.SwapUsedMB)} / ${formatMB(snapshot.SwapTotalMB)}`
                      : ""
                  }`
                : "No data"
            }
            progress={snapshot?.MemoryPercent}
            note={trend("memory")}
          >
            <Sparkline values={history.map((point) => point.memory)} />
          </MetricCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<StorageRoundedIcon />}
            title="Disk"
            value={formatPercent(snapshot?.DiskPercent)}
            subtitle={
              snapshot
                ? `${formatGB(snapshot.DiskUsedGB)} of ${formatGB(snapshot.DiskTotalGB)} · ${snapshot.DiskMount || "/"}`
                : "No data"
            }
            progress={snapshot?.DiskPercent}
            note={trend("disk")}
          >
            <Sparkline values={history.map((point) => point.disk)} />
          </MetricCard>
        </Grid>

        {/* Services --------------------------------------------------------- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard icon={<DnsRoundedIcon />} title="Services">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
              {services.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No services probed.
                </Typography>
              )}
              {services.map((service) => (
                <Box
                  key={service.name}
                  sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70 }}>
                    {service.name}
                  </Typography>
                  <StatusChip status={service.status} />
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 140 }}>
                    {service.detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          </MetricCard>
        </Grid>

        {/* SSL -------------------------------------------------------------- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard icon={<LockRoundedIcon />} title="SSL certificates">
            {!config?.sslConfigured && (
              <Typography variant="body2" color="text.secondary">
                Not configured. Add <code>SERVER_SSL_CERTS</code> to the server
                environment to monitor certificate expiry.
              </Typography>
            )}

            {config?.sslConfigured && certificates.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No certificate data in the latest snapshot.
              </Typography>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {certificates.map((certificate) => {
                const color =
                  certificate.daysRemaining === null || certificate.status === "error"
                    ? theme.palette.grey[500]
                    : certificate.daysRemaining < 0
                      ? theme.palette.error.main
                      : certificate.daysRemaining <= 14
                        ? theme.palette.warning.main
                        : theme.palette.success.main;

                return (
                  <Box key={certificate.domain} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {certificate.domain}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          certificate.status === "error"
                            ? "Error"
                            : certificate.daysRemaining < 0
                              ? "Expired"
                              : `${certificate.daysRemaining} days left`
                        }
                        sx={{
                          height: 22,
                          borderRadius: "8px",
                          fontWeight: 600,
                          bgcolor: `${color}1a`,
                          color,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {certificate.notAfter
                        ? `Expires ${new Date(certificate.notAfter).toLocaleDateString()}${certificate.issuer ? ` · ${certificate.issuer}` : ""}`
                        : certificate.error || "Expiry unknown"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </MetricCard>
        </Grid>

        {/* Backups ---------------------------------------------------------- */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<BackupRoundedIcon />}
            title="DB backups"
            value={
              backups?.latest
                ? `${backups.latest.ageHours}h old`
                : backups?.status === "unavailable"
                  ? "—"
                  : "None"
            }
            subtitle={
              backups?.latest
                ? `${(backups.latest.sizeBytes / 1024 / 1024).toFixed(1)} MB · ${backups.fileCount ?? 0} file(s)`
                : backups?.error || backups?.directory || ""
            }
            note={backups?.latest?.name || null}
          >
            {backups?.status && <StatusChip status={backups.status} />}

            {/* The five newest dumps — enough to see whether the schedule is
                actually producing files, without leaving this tab. */}
            {Array.isArray(backups?.files) && backups.files.length > 1 && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 0.4, mt: 0.5 }}
              >
                {backups.files.slice(1).map((file) => (
                  <Box
                    key={file.path}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ flex: 1, minWidth: 0, fontFamily: "monospace", fontSize: "0.68rem" }}
                    >
                      {file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0, fontSize: "0.68rem" }}
                    >
                      {file.ageHours}h · {formatBytes(file.sizeBytes)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </MetricCard>
        </Grid>

        {/* Host ------------------------------------------------------------- */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<ScheduleRoundedIcon />}
            title="Host"
            value={formatDuration(snapshot?.UptimeSeconds)}
            subtitle={snapshot?.Hostname || "No hostname reported"}
            note={snapshot?.CapturedAt ? `Collected ${relativeTime(snapshot.CapturedAt)}` : "No data"}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
              <Typography variant="caption" color="text.secondary">
                Load {snapshot?.LoadAvg1 ?? "—"} / {snapshot?.LoadAvg5 ?? "—"} /{" "}
                {snapshot?.LoadAvg15 ?? "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Swap{" "}
                {snapshot?.SwapTotalMB
                  ? `${formatMB(snapshot.SwapUsedMB)} / ${formatMB(snapshot.SwapTotalMB)}`
                  : "not configured"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Snapshots kept {config?.metricsRetentionDays ?? 14} days
              </Typography>
            </Box>
          </MetricCard>
        </Grid>

        {/* Offsite quota — the number that was invisible while the archive
            silently filled up. Only rendered when a remote is configured. */}
        {config?.rcloneConfigured && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              icon={<CloudQueueRoundedIcon />}
              title="Offsite storage"
              value={
                drive?.status === "ok"
                  ? formatPercent(drive.usedPercent)
                  : drive?.status === "unlimited"
                    ? "Unlimited"
                    : drive?.status === "unconfigured"
                      ? "—"
                      : "Unavailable"
              }
              subtitle={
                drive?.status === "ok"
                  ? `${formatBytes(drive.usedBytes)} of ${formatBytes(drive.totalBytes)} · ${formatBytes(drive.freeBytes)} free`
                  : drive?.status === "unlimited"
                    ? `${formatBytes(drive.usedBytes)} used · no quota reported`
                    : drive?.remote || config?.rcloneRemote || ""
              }
              note={
                drive?.status === "error"
                  ? drive.error
                  : driveTrend?.growthBytesPerDay !== null &&
                      driveTrend?.growthBytesPerDay !== undefined
                    ? driveTrend.growthBytesPerDay > 0
                      ? `Growing ${formatBytes(driveTrend.growthBytesPerDay)}/day${
                          driveTrend.daysUntilFull !== null
                            ? ` · ~${driveTrend.daysUntilFull} day(s) until full`
                            : ""
                        }`
                      : "Not growing over the last week"
                    : null
              }
              progress={drive?.status === "ok" ? drive.usedPercent : undefined}
            >
              {drive?.status && (
                <StatusChip
                  status={drive.status === "unlimited" ? "ok" : drive.status}
                  label={
                    drive.status === "ok"
                      ? "Quota reported"
                      : drive.status === "unlimited"
                        ? "No quota"
                        : drive.status === "unconfigured"
                          ? "Not configured"
                          : "Unreachable"
                  }
                />
              )}

              {drive?.trashedPercent !== null &&
                drive?.trashedPercent !== undefined &&
                drive.trashedPercent >= 5 && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.warning.main, mt: 0.5 }}
                  >
                    {formatBytes(drive.trashedBytes)} in trash still counts against
                    the quota — empty it to reclaim space.
                  </Typography>
                )}

              {drive?.status === "error" && drive?.raw && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ fontFamily: "monospace", fontSize: "0.68rem", opacity: 0.8 }}
                >
                  {drive.raw}
                </Typography>
              )}
            </MetricCard>
          </Grid>
        )}

        {/* Database summary — the full breakdown lives in the Database tab */}
        {database?.totals && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              icon={<TableChartRoundedIcon />}
              title="Database"
              value={`${Number(database.totals.tables).toLocaleString()} tables`}
              subtitle={`~${Number(
                database.totals.rowEstimate
              ).toLocaleString()} rows · ${(
                Number(database.totals.totalBytes) /
                1024 /
                1024
              ).toFixed(1)} MB · ${database.database || ""}`}
            >
              <Typography variant="caption" color="text.secondary">
                {database.pool
                  ? `Pool: ${database.pool.using ?? 0} in use · ${database.pool.available ?? 0} idle${
                      database.pool.waiting > 0
                        ? ` · ${database.pool.waiting} waiting`
                        : ""
                    }`
                  : "See the Database tab for the per-table breakdown."}
              </Typography>
            </MetricCard>
          </Grid>
        )}
      </Grid>

      <Divider />
      <Typography variant="caption" color="text.secondary">
        Collected by the server's cron job and stored in the Portal database, so
        this page still renders when the application itself is down.
        {config?.staleAfterSeconds
          ? ` A snapshot older than ${config.staleAfterSeconds}s is flagged as stale.`
          : ""}
      </Typography>
    </Box>
  );
};

export default ServerOverview;
